// src/pages/admin/AdminAuditLogs.js
// Logs d'audit - historique des actions admin (SUPER_ADMIN) - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import { AUDIT_ACTION_DESCRIPTIONS } from '../../utils/auditLog';

const AdminAuditLogs = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // Données de démo (à remplacer par adminAuditLogsAPI.getAll())
    const demoLogs = [
      { id: 'log_1', action: 'listing_published', adminName: 'Yvan Karel', adminRole: 'SUPER_ADMIN', targetName: 'Appartement moderne meublé', targetType: 'listing', timestamp: '2026-07-01T10:30:00', status: 'success' },
      { id: 'log_2', action: 'reservation_status_changed', adminName: 'Admin Support', adminRole: 'SUPPORT', targetName: 'Réservation #101', targetType: 'reservation', timestamp: '2026-07-01T09:15:00', status: 'success' },
      { id: 'log_3', action: 'listing_created', adminName: 'Éditeur Contenu', adminRole: 'EDITOR', targetName: 'Studio meublé centre-ville', targetType: 'listing', timestamp: '2026-06-30T16:45:00', status: 'success' },
      { id: 'log_4', action: 'admin_created', adminName: 'Yvan Karel', adminRole: 'SUPER_ADMIN', targetName: 'editor@fynho.com', targetType: 'admin', timestamp: '2026-06-30T14:20:00', status: 'success' },
      { id: 'log_5', action: 'listing_rejected', adminName: 'Yvan Karel', adminRole: 'SUPER_ADMIN', targetName: 'Chambre insalubre', targetType: 'listing', timestamp: '2026-06-29T11:00:00', status: 'success' },
      { id: 'log_6', action: 'login', adminName: 'Admin Support', adminRole: 'SUPPORT', targetName: '', targetType: '', timestamp: '2026-06-29T08:00:00', status: 'success' },
    ];
    setLogs(demoLogs);
  }, []);

  const filterOptions = [
    { id: 'all', label: 'Tous' },
    { id: 'listing', label: 'Annonces' },
    { id: 'reservation', label: 'Réservations' },
    { id: 'admin', label: 'Admins' },
  ];

  const filteredLogs = logs.filter((log) => filter === 'all' || log.targetType === filter);

  const getActionIcon = (action) => {
    if (action.includes('created') || action.includes('published')) return '✅';
    if (action.includes('deleted') || action.includes('rejected')) return '❌';
    if (action.includes('updated') || action.includes('changed')) return '✏️';
    if (action.includes('login')) return '🔓';
    if (action.includes('logout')) return '🔒';
    return '📝';
  };

  const getRoleColor = (role) => {
    const colors = {
      SUPER_ADMIN: '#FFD700',
      ADMIN: '#48BB78',
      EDITOR: '#4299E1',
      SUPPORT: '#ED8936',
    };
    return colors[role] || '#A0AEC0';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '900px', margin: '0 auto', backgroundColor: '#0A0E1A' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', position: 'sticky', top: 0, backgroundColor: '#0A0E1A', zIndex: 50 },
    backButton: { background: 'none', border: 'none', color: '#FFFFFF', fontSize: '20px', cursor: 'pointer' },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF' },
    filters: { display: 'flex', gap: '8px', overflowX: 'auto', padding: '16px 20px', scrollbarWidth: 'none' },
    filterBtn: { padding: '8px 14px', borderRadius: '20px', backgroundColor: '#162033', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#A0AEC0', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
    filterBtnActive: { backgroundColor: 'rgba(255, 215, 0, 0.15)', border: '1px solid rgba(255, 215, 0, 0.3)', color: '#FFD700' },
    list: { display: 'flex', flexDirection: 'column', gap: '10px', padding: '0 20px 40px' },
    logCard: { display: 'flex', gap: '12px', padding: '14px', backgroundColor: '#162033', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.1)' },
    logIcon: { fontSize: '20px', flexShrink: 0 },
    logBody: { flex: 1 },
    logAction: { fontSize: '14px', fontWeight: '600', color: '#FFFFFF' },
    logTarget: { fontSize: '13px', color: '#A0AEC0', marginTop: '2px' },
    logMeta: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' },
    logAdmin: { fontSize: '12px', fontWeight: '600' },
    logTime: { fontSize: '11px', color: '#4A5568' },
    empty: { textAlign: 'center', padding: '48px', color: '#A0AEC0', fontSize: '14px' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Logs d'audit | Fynho Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}>←</button>
        <div style={styles.title}>Logs d'audit</div>
      </div>

      <div style={styles.filters} className="hide-scrollbar">
        <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
        {filterOptions.map((f) => (
          <button
            key={f.id}
            style={{ ...styles.filterBtn, ...(filter === f.id ? styles.filterBtnActive : {}) }}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div style={styles.list}>
        {filteredLogs.length === 0 ? (
          <div style={styles.empty}>Aucun log trouvé</div>
        ) : (
          filteredLogs.map((log) => (
            <div key={log.id} style={styles.logCard}>
              <span style={styles.logIcon}>{getActionIcon(log.action)}</span>
              <div style={styles.logBody}>
                <div style={styles.logAction}>
                  {AUDIT_ACTION_DESCRIPTIONS[log.action] || log.action}
                </div>
                {log.targetName && <div style={styles.logTarget}>{log.targetName}</div>}
                <div style={styles.logMeta}>
                  <span style={{ ...styles.logAdmin, color: getRoleColor(log.adminRole) }}>
                    {log.adminName}
                  </span>
                  <span style={styles.logTime}>· {formatTimestamp(log.timestamp)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminAuditLogs;